import { ReactNode } from "react";
import { Box, Container, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { fontStack } from "@/theme";

interface LegalPageLayoutProps {
  title: string;
  lastUpdated: string;
  children: ReactNode;
}

/**
 * Shared chrome for /privacy and /terms. Matches /welcome's editorial
 * aesthetic — warm cream, serif headline, mono wordmark, orange
 * accent — so these pages feel part of the app, not afterthoughts.
 */
export default function LegalPageLayout({
  title,
  lastUpdated,
  children,
}: LegalPageLayoutProps) {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 8, md: 12 } }}>
        <Typography
          component={RouterLink}
          to="/"
          sx={{
            fontFamily: fontStack.mono,
            fontSize: 12,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "ink.muted",
            display: "inline-block",
            mb: { xs: 4, md: 6 },
            textDecoration: "none",
            "&:hover": { color: "ink.main" },
          }}
        >
          howisthe.surf
        </Typography>

        <Typography
          component="h1"
          sx={{
            fontFamily: fontStack.serif,
            fontWeight: 500,
            fontSize: { xs: 36, md: 52 },
            lineHeight: 1.1,
            letterSpacing: "-0.01em",
            color: "ink.main",
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontFamily: fontStack.mono,
            fontSize: 12,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "ink.muted",
            mb: { xs: 5, md: 8 },
          }}
        >
          Last updated {lastUpdated}
        </Typography>

        {/* The legal prose gets generic typographic defaults via `.prose`
            below rather than wrapping every paragraph. */}
        <Box
          className="prose"
          sx={{
            fontFamily: fontStack.sans,
            fontSize: 16,
            lineHeight: 1.65,
            color: "ink.main",
            "& h2": {
              fontFamily: fontStack.serif,
              fontWeight: 500,
              fontSize: { xs: 22, md: 26 },
              letterSpacing: "-0.005em",
              color: "ink.main",
              mt: 5,
              mb: 2,
              lineHeight: 1.25,
            },
            "& h3": {
              fontFamily: fontStack.serif,
              fontWeight: 500,
              fontSize: 18,
              color: "ink.main",
              mt: 3,
              mb: 1,
            },
            "& p": {
              margin: "0 0 1rem",
              color: "ink.main",
            },
            "& ul": {
              margin: "0 0 1rem",
              paddingLeft: "1.25rem",
              color: "ink.main",
            },
            "& li": {
              marginBottom: "0.4rem",
            },
            "& a": {
              color: "#E5541B",
              textDecoration: "underline",
              textUnderlineOffset: "3px",
            },
            "& strong": { fontWeight: 600 },
            "& code": {
              fontFamily: fontStack.mono,
              fontSize: "0.9em",
              background: "rgba(26, 42, 54, 0.06)",
              padding: "0.1em 0.3em",
              borderRadius: 3,
            },
          }}
        >
          {children}
        </Box>

        <Box
          sx={{
            mt: { xs: 8, md: 12 },
            pt: 4,
            borderTop: "1px solid",
            borderColor: "rule.main",
            fontFamily: fontStack.mono,
            fontSize: 12,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: "ink.muted",
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          <RouterLink
            to="/"
            style={{ color: "inherit", textDecoration: "none" }}
            onMouseOver={(e) => (e.currentTarget.style.color = "#0E1B26")}
            onMouseOut={(e) => (e.currentTarget.style.color = "")}
          >
            ← Back to the surf
          </RouterLink>
          <RouterLink to="/privacy" style={{ color: "inherit", textDecoration: "none" }}>
            Privacy
          </RouterLink>
          <RouterLink to="/terms" style={{ color: "inherit", textDecoration: "none" }}>
            Terms
          </RouterLink>
        </Box>
      </Container>
    </Box>
  );
}
